export type CreditUnionCalculatorInputs = {
  totalMembers: number;
  currentRate: number;
  targetRate: number;
  avgProducts: number;
  avgLoan: number;
  avgDeposit: number;
};

export type CreditUnionCalculatorResults = {
  unengaged: number;
  incrementalMembers: number;
  newProducts: number;
  depositGrowth: number;
  loanOrigination: number;
  callDeflection: number;
  projection: number[];
};

export const DEFAULT_INPUTS: CreditUnionCalculatorInputs = {
  totalMembers: 25000,
  currentRate: 10,
  targetRate: 35,
  avgProducts: 2.4,
  avgLoan: 12000,
  avgDeposit: 8000,
};

/** Abbreviates large dollar amounts to K/M/B suffixes for compact display (e.g. $1.2M). */
export const formatCompactDollar = (amount: number): string => {
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${Math.round(amount)}`;
};

/** Formats a number as a locale-aware integer string (e.g. 1,234). */
export const formatCount = (value: number): string => Math.round(value).toLocaleString("en-US");

/** Estimates the engagement impact of moving from the current wellness participation rate to the target rate, projecting new product relationships, deposit growth, loan origination, and call deflection. */
export const calcResults = (inputs: CreditUnionCalculatorInputs): CreditUnionCalculatorResults => {
  const { totalMembers, currentRate, targetRate, avgProducts, avgLoan, avgDeposit } = inputs;
  const currentEngagementRatio = currentRate / 100;
  const targetEngagementRatio = targetRate / 100;
  const currentlyEngaged = totalMembers * currentEngagementRatio;
  const unengaged = totalMembers - currentlyEngaged;
  const incrementalMembers = Math.max(0, totalMembers * targetEngagementRatio - currentlyEngaged);
  const newProducts = incrementalMembers * avgProducts;
  const depositGrowth = incrementalMembers * avgDeposit * 0.15;
  const loanOrigination = incrementalMembers * 0.08 * avgLoan;
  const callDeflection = incrementalMembers * 3;
  const baseEngaged = currentlyEngaged + incrementalMembers;
  const projection = Array.from({ length: 3 }, (_, yearIndex) =>
    Math.min(baseEngaged * Math.pow(1.2, yearIndex), totalMembers)
  );
  return { unengaged, incrementalMembers, newProducts, depositGrowth, loanOrigination, callDeflection, projection };
};
